import IntlMessages from '@crema/utility/IntlMessages'
import { Alert } from '@material-ui/lab'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Grid from '@material-ui/core/Grid'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'
import React, { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { addonFieldAddThunk, addonFieldListThunk, selectAllAddon, selectAddonApiStatus } from 'store/reducers/temple/offeringAddonSlice'
import { apiStatus } from 'shared/constants/AppEnum'
import { IOfferingResponse } from 'types/api/temple'
import { TAdditionalField } from 'types/store/temple'
import { extractError } from 'utils/api'
import { defaultNewField } from 'utils/temple/offering'
import { addonFieldTypes, addonFieldValidationSchema, buildAddonPayload } from '../Bookable/FieldsForm'
import { extractIdFromUrlRegex } from 'shared/constants/AppConst'

interface BulkOfferingAddonFieldDialogProps {
    open: boolean
    offerings: IOfferingResponse[]
    onClose: () => void
    onComplete?: () => void
}

const BulkOfferingAddonFieldDialog: React.FC<BulkOfferingAddonFieldDialogProps> = ({ open, offerings, onClose, onComplete }) => {
    const dispatch = useAppDispatch()
    const api = useAppSelector(selectAddonApiStatus)
    const allAddonFields = useAppSelector(selectAllAddon)
    const { enqueueSnackbar } = useSnackbar()
    const [submitting, setSubmitting] = useState(false)
    const [loadingExistingFields, setLoadingExistingFields] = useState(false)
    const offeringIdsKey = useMemo(() => offerings.map(offering => offering.id).join(','), [offerings])

    const { values, setFieldValue, resetForm, errors, handleSubmit, dirty } = useFormik<TAdditionalField>({
        initialValues: defaultNewField,
        enableReinitialize: true,
        validationSchema: addonFieldValidationSchema,
        validateOnChange: true,
        onSubmit: async (formValues) => {
            if (offerings.length === 0) return

            setSubmitting(true)
            try {
                const targets = offerings.filter(offering => !allAddonFields.some(field =>
                    (field.offering === offering.url || field.offering.match(extractIdFromUrlRegex)?.[1] === String(offering.id)) &&
                    field.fieldName.trim().toLowerCase() === formValues.fieldName.trim().toLowerCase() &&
                    field.dataType === formValues.fieldType
                ))

                if (targets.length > 0) {
                    await Promise.all(targets.map(offering =>
                        dispatch(addonFieldAddThunk(buildAddonPayload(formValues, offering.url))).then(unwrapResult)
                    ))
                }

                const skippedCount = offerings.length - targets.length
                enqueueSnackbar(
                    `Added addon field to ${targets.length} offering${targets.length === 1 ? '' : 's'}${skippedCount ? `, skipped ${skippedCount} duplicate${skippedCount === 1 ? '' : 's'}` : ''}`,
                    { variant: 'success' }
                )
                resetForm()
                onComplete?.()
                onClose()
            } catch (error) {
                enqueueSnackbar(extractError(error) ?? 'Failed to add addon field', { variant: 'error' })
            } finally {
                setSubmitting(false)
            }
        }
    })

    useEffect(() => {
        if (open) resetForm()
    }, [open, resetForm])

    useEffect(() => {
        if (!open || !offeringIdsKey) return

        setLoadingExistingFields(true)
        const offeringIds = offeringIdsKey.split(',')
        Promise.allSettled(
            offeringIds.map(offeringId => dispatch(addonFieldListThunk(offeringId)))
        ).finally(() => setLoadingExistingFields(false))
    }, [dispatch, offeringIdsKey, open])

    const handleFieldTypeChange = (fieldType: string) => {
        setFieldValue('fieldType', fieldType)
        if (fieldType !== 'str') {
            setFieldValue('minValue', values?.minValue ? fieldType === 'int' ? Math.floor(Number(values.minValue)) : values.minValue : undefined)
            setFieldValue('maxValue', values?.maxValue ? fieldType === 'int' ? Math.ceil(Number(values.maxValue)) : values.maxValue : undefined)
            setFieldValue('minLength', undefined)
            setFieldValue('maxLength', undefined)
        } else {
            setFieldValue('minLength', values?.minLength ? Math.floor(Number(values.minLength)) : undefined)
            setFieldValue('maxLength', values?.maxLength ? Math.floor(Number(values.maxLength)) : undefined)
            setFieldValue('minValue', undefined)
            setFieldValue('maxValue', undefined)
        }
        if (fieldType !== 'float') {
            setFieldValue('addToPrice', false)
            setFieldValue('allowMultiplier', false)
        }
    }

    const isSubmitting = submitting || loadingExistingFields || api.status === apiStatus.LOADING

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Bulk Add Addon Field</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Box mb={4}>
                        <Typography variant="body2" color="textSecondary">
                            Add this addon field to {offerings.length} selected offering{offerings.length === 1 ? '' : 's'}.
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                required
                                label={<IntlMessages id='offering.additional.fieldName' />}
                                onChange={(e) => setFieldValue('fieldName', e.target.value)}
                                variant='outlined'
                                fullWidth
                                value={values.fieldName}
                                size='small'
                                helperText={errors.fieldName}
                                error={Boolean(errors?.fieldName)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                required
                                label={<IntlMessages id='offering.additional.fieldType' />}
                                variant='outlined'
                                onChange={(e) => handleFieldTypeChange(e.target.value)}
                                fullWidth
                                value={values.fieldType}
                                helperText={errors.fieldType}
                                error={Boolean(errors?.fieldType)}
                                size='small'
                            >
                                {addonFieldTypes.map(f => (
                                    <MenuItem value={f.type} key={f.label}>
                                        {f.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                label={<IntlMessages id='common.description' />}
                                onChange={(e) => setFieldValue('description', e.target.value)}
                                variant='outlined'
                                fullWidth
                                value={values.description}
                                size='small'
                                helperText={errors.description}
                                error={Boolean(errors?.description)}
                            />
                        </Grid>
                        {values.fieldType === 'str' ? (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        onChange={(e) => setFieldValue('minLength', e.target.value && Number(e.target.value) > 0 ? Math.floor(Number(e.target.value)) : e.target.value)}
                                        label={<IntlMessages id='offering.additional.minLength' />}
                                        variant='outlined'
                                        type='number'
                                        fullWidth
                                        value={values.minLength}
                                        size='small'
                                        helperText={errors.minLength}
                                        error={Boolean(errors?.minLength)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        label={<IntlMessages id='offering.additional.maxLength' />}
                                        variant='outlined'
                                        fullWidth
                                        type='number'
                                        onChange={(e) => setFieldValue('maxLength', e.target.value && Number(e.target.value) > 0 ? Math.ceil(Number(e.target.value)) : e.target.value)}
                                        value={values.maxLength}
                                        size='small'
                                        helperText={errors.maxLength}
                                        error={Boolean(errors?.maxLength)}
                                    />
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        label={<IntlMessages id='offering.additional.minValue' />}
                                        variant='outlined'
                                        onChange={(e) => setFieldValue('minValue', values.fieldType === 'int' && e.target.value && Number(e.target.value) > 0 ? Math.floor(Number(e.target.value)) : e.target.value)}
                                        fullWidth
                                        type='number'
                                        value={values.minValue}
                                        size='small'
                                        helperText={errors.minValue}
                                        error={Boolean(errors?.minValue)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        label={<IntlMessages id='offering.additional.maxValue' />}
                                        variant='outlined'
                                        onChange={(e) => setFieldValue('maxValue', values.fieldType === 'int' && e.target.value && Number(e.target.value) > 0 ? Math.ceil(Number(e.target.value)) : e.target.value)}
                                        fullWidth
                                        type='number'
                                        value={values.maxValue}
                                        size='small'
                                        helperText={errors.maxValue}
                                        error={Boolean(errors?.maxValue)}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            {values.fieldType === 'float' && (
                                <FormControlLabel
                                    control={<Checkbox checked={values.allowMultiplier} name="allowMultiplier" size='small' onChange={(e) => setFieldValue('allowMultiplier', e.target.checked)} />}
                                    label="Use Multiplier"
                                />
                            )}
                            {values.fieldType === 'float' && (
                                <FormControlLabel
                                    control={<Checkbox checked={values.addToPrice} name="addToPrice" size='small' onChange={(e) => setFieldValue('addToPrice', e.target.checked)} />}
                                    label="Add to Price"
                                />
                            )}
                            <FormControlLabel
                                control={<Checkbox checked={values.required} name="required" size='small' onChange={(e) => setFieldValue('required', e.target.checked)} />}
                                label="Required"
                            />
                        </Grid>
                        {values.allowMultiplier && (
                            <Grid item xs={12}>
                                <TextField
                                    InputLabelProps={{ shrink: true }}
                                    label="Multiplier Label"
                                    onChange={(e) => setFieldValue('multiplierLabel', e.target.value)}
                                    variant='outlined'
                                    fullWidth
                                    value={values.multiplierLabel}
                                    size='small'
                                    helperText={errors.multiplierLabel}
                                    error={Boolean(errors?.multiplierLabel)}
                                />
                            </Grid>
                        )}
                        {api.status === apiStatus.FAILED && (
                            <Grid item xs={12}>
                                <Alert severity="error">{extractError(api.error)}</Alert>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    {isSubmitting && <CircularProgress size="small" />}
                    <Button onClick={onClose} color="primary" variant="outlined" disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button color="primary" variant="contained" type="submit" disabled={!dirty || isSubmitting || offerings.length === 0}>
                        {loadingExistingFields ? 'Loading...' : 'Apply'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}

export default BulkOfferingAddonFieldDialog
